import {
  ColumnHeader,
  ColumnLeft,
  ColumnRight,
  Icon,
  Loader,
  TwoColumnPanel
} from '@deque/cauldron-react';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, Switch, useLocation } from 'react-router-dom';
import ContentToast from '~/common/components/ContentToast';
import NavLink from '~/common/components/Link';
import PageTitle from '~/common/components/PageTitle';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import { type AuthUser, useAuthContext } from '~/common/contexts/auth';
import { type AllSettingsSchema } from '~/common/contexts/Configuration';
import commonStyles from '~/common/utils/common.css';
import { type Errors } from '~/common/utils/validate-general-settings-form';
import styles from './AccountSettings.css';
import GeneralAccountSettings from './General';
import PasswordAccountSettings from './Password';

interface AccountSettingsProps {
  children?: React.ReactNode;
  loading: boolean;
  error: Error | string | null;
  submitting: boolean;
  narrow: boolean;
  user?: AuthUser;
  settings: AllSettingsSchema | null;
  handleGeneralSubmit?: (event: React.ChangeEvent<HTMLFormElement>) => void;
  handlePasswordSubmit?: (event: React.ChangeEvent<HTMLFormElement>) => void;
  firstNameRef?: React.RefObject<HTMLInputElement | null>;
  lastNameRef?: React.RefObject<HTMLInputElement | null>;
  emailRef?: React.RefObject<HTMLInputElement | null>;
  jobFunctionRef?: React.RefObject<HTMLSelectElement | null>;
  languageRef?: React.RefObject<HTMLSelectElement | null>;
  currentPasswordRef?: React.RefObject<HTMLInputElement | null>;
  passwordRef?: React.RefObject<HTMLInputElement | null>;
  confirmPasswordRef?: React.RefObject<HTMLInputElement | null>;
  generalErrors: Errors;
  passwordErrors: Errors;
  shouldRestrictEmail?: boolean;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({
  error,
  loading,
  submitting,
  narrow,
  user,
  settings,
  handleGeneralSubmit,
  handlePasswordSubmit,
  emailRef,
  firstNameRef,
  lastNameRef,
  jobFunctionRef,
  languageRef,
  currentPasswordRef,
  passwordRef,
  confirmPasswordRef,
  generalErrors,
  passwordErrors,
  shouldRestrictEmail
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { createAccountUrl } = useAuthContext();
  const loaderRef = useRef<HTMLDivElement>(null);

  const accountUrl = createAccountUrl && createAccountUrl();

  const paths = useMemo(() => {
    const { pathname } = location;
    return {
      accountIsActive: pathname === '/account-settings',
      passwordIsActive: pathname === '/account-settings/password'
    };
  }, [location]);

  if (error) {
    return (
      <ContentToast show={true} type="caution">
        {t('Failed to load account settings.')}
      </ContentToast>
    );
  }

  if (loading) {
    return (
      <Loader
        label={t('Loading account settings...')}
        tabIndex={-1}
        ref={loaderRef}
      />
    );
  }

  return (
    <>
      <PageTitle title={t('Account Settings')} />
      <div
        className={classNames(styles.wrap, commonStyles.resetFieldsets, {
          // This is managed in JS, rather than CSS to help keep in sync with the panel breakpoint
          [styles.collapsed]: narrow
        })}
      >
        <h1>{t('Account Settings')}</h1>
        <TwoColumnPanel>
          <ColumnLeft aria-labelledby="account-settings-sidebar-header">
            <ColumnHeader
              id="account-settings-sidebar-header"
              className={styles.columnHeader}
            >
              <h2>{t('Settings')}</h2>
            </ColumnHeader>
            <nav aria-labelledby="account-settings-heading">
              <ul>
                <li>
                  <Link
                    to="/account-settings"
                    aria-current={paths.accountIsActive}
                  >
                    {t('General')}
                    <em>{t('Manage general account settings.')}</em>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/account-settings/password"
                    aria-current={paths.passwordIsActive}
                  >
                    {t('Password')}
                    <em>{t('Manage and change your password.')}</em>
                  </Link>
                </li>
                <li>
                  <NavLink
                    url={
                      accountUrl ? `${accountUrl}&origin=account_portal` : '/'
                    }
                  >
                    <span>
                      {t('Advanced Settings')}{' '}
                      <Icon
                        type="external-link"
                        label={t('Opens in new window')}
                        className={styles.externalLink}
                      />
                    </span>
                    <em>{t('Manage additional account settings.')}</em>
                  </NavLink>
                </li>
              </ul>
            </nav>
          </ColumnLeft>
          <ColumnRight aria-labelledby="account-settings-item-header">
            {submitting && (
              <ScrimmedLoader label={t('Saving account settings...')} />
            )}
            <Switch>
              <Route exact path="/account-settings">
                <GeneralAccountSettings
                  user={user}
                  settings={settings}
                  submitting={submitting}
                  handleGeneralSubmit={handleGeneralSubmit}
                  emailRef={emailRef}
                  firstNameRef={firstNameRef}
                  lastNameRef={lastNameRef}
                  jobFunctionRef={jobFunctionRef}
                  languageRef={languageRef}
                  errors={generalErrors}
                  shouldRestrictEmail={shouldRestrictEmail}
                />
              </Route>
              <Route exact path="/account-settings/password">
                <PasswordAccountSettings
                  submitting={submitting}
                  handlePasswordSubmit={handlePasswordSubmit}
                  currentPasswordRef={currentPasswordRef}
                  passwordRef={passwordRef}
                  confirmPasswordRef={confirmPasswordRef}
                  errors={passwordErrors}
                />
              </Route>
            </Switch>
          </ColumnRight>
        </TwoColumnPanel>
      </div>
    </>
  );
};

export default AccountSettings;
